import { useState, useEffect, useRef, useMemo } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "react-query";
import {
  ArrowLeft,
  Save,
  CheckCircle,
  ChevronLeft,
  ChevronRight,
  Edit,
  Lock,
  ClipboardList,
  PlayCircle,
  Eye,
  Users,
} from "lucide-react";
import toast from "react-hot-toast";
import { apiService } from "../services/api";
import { useAuth } from "../hooks/useAuth";
import { canManageAssessment } from "../types";
import { canReopenSubmittedAssessment } from "../utils/sdlAssessmentEditing";
import SDLAssessmentQuestion from "../components/SDLAssessmentQuestion";
import SDLAssessmentFeedback from "../components/SDLAssessmentFeedback";
import ParticipantMultiSelect from "../components/ParticipantMultiSelect";
import {
  participantIdsFrom,
  participantLabel,
  type AssessmentParticipant,
} from "../utils/assessmentParticipants";

interface AnswerState {
  score: number | null; // 0-4 for maturity, -1 for N/A, null for unanswered
  details: string;
}

export default function SDLAssessmentPageSimple() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const periodFromUrl = searchParams.get("period");
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const loadedAssessmentRef = useRef<string | null>(null);
  const loadedPeriodRef = useRef<string | null>(null);
  const [activeTab, setActiveTab] = useState(0);
  const [selectedPeriod, setSelectedPeriod] = useState<string | null>(
    periodFromUrl,
  );
  const [existingAssessmentId, setExistingAssessmentId] = useState<
    string | null
  >(null);
  const [assessmentStarted, setAssessmentStarted] = useState(false);

  const [answers, setAnswers] = useState<Record<string, AnswerState>>({});
  const [previousAnswers, setPreviousAnswers] = useState<
    Record<string, AnswerState>
  >({});
  const [previousPeriod, setPreviousPeriod] = useState<string | null>(null);
  const [showFeedback, setShowFeedback] = useState(false);
  const [submittedScore, setSubmittedScore] = useState<number>(0);
  const [participantIds, setParticipantIds] = useState<string[]>([]);
  // Participants are captured up front on the start screen, so in the question
  // view they collapse to a one-line summary rather than permanently taking
  // vertical space away from the question being answered.
  const [participantsExpanded, setParticipantsExpanded] = useState(false);

  const { data: productData, isLoading: productLoading } = useQuery(
    ["product", id],
    () => apiService.getProduct(id!),
    {
      enabled: !!id,
    },
  );

  const { data: periodsData, isLoading: periodsLoading } = useQuery(
    ["sdlAssessmentPeriods", id],
    () => apiService.getSDLAssessmentPeriodsForProduct(id!),
    { enabled: !!id },
  );

  const { data: questionsData } = useQuery(
    "sdlQuestions",
    apiService.getSDLQuestions,
    {
      enabled: !!id,
      staleTime: 5 * 60 * 1000,
    },
  );

  // Directory for the participant picker. Shared across assessments, so it is
  // cached for the session rather than refetched per period.
  const { data: usersData, isLoading: usersLoading } = useQuery(
    "users",
    apiService.getUsers,
    { staleTime: 5 * 60 * 1000 },
  );

  const {
    data: existingAssessmentData,
    isError: assessmentNotFound,
    isLoading: assessmentLoading,
    isFetching: assessmentFetching,
  } = useQuery(
    ["sdlAssessment", id, selectedPeriod],
    () => apiService.getSDLAssessmentByPeriod(id!, selectedPeriod!),
    {
      enabled: !!id && !!selectedPeriod,
      retry: false,
      keepPreviousData: false,
      cacheTime: 0,
    },
  );

  // Fetch the most recent submitted assessment before the selected period
  const { data: previousAssessmentData } = useQuery(
    ["sdlPreviousAssessment", id, selectedPeriod],
    () =>
      apiService
        .getPreviousSubmittedSDLAssessment(id!, selectedPeriod!)
        .catch(() => ({ data: { success: true, data: null } })),
    {
      enabled: !!id && !!selectedPeriod,
      retry: false,
      staleTime: 5 * 60 * 1000,
    },
  );

  const product = productData?.data?.data;
  const periods = ((periodsData?.data as any)?.data || []) as any[];

  const apiQuestions =
    (((questionsData?.data as any)?.data?.questions || []) as any[]) || [];
  const uiQuestions = useMemo(
    () =>
      apiQuestions.map((q: any) => ({
        id: String(q.id),
        name: q.name,
        description: q.description,
        practice: q.practice,
        criteria: q.criteria || "",
      })),
    [JSON.stringify(apiQuestions.map((q: any) => q.id))],
  );

  const openPeriods = periods.filter((p) => p.isOpen);
  const existingAssessment = assessmentNotFound
    ? null
    : (existingAssessmentData?.data as any)?.data;
  const isViewOnly = existingAssessment?.status === "submitted";
  const selectedPeriodData = periods.find(
    (p: any) => p.period === selectedPeriod,
  );
  const hasAssessmentForPeriod =
    selectedPeriodData?.hasAssessment &&
    !assessmentNotFound &&
    !!existingAssessment;
  const isLockedDraft =
    hasAssessmentForPeriod &&
    existingAssessment?.status === "draft" &&
    !selectedPeriodData?.isOpen;
  const canEdit = !isViewOnly && !isLockedDraft;
  // Non-owners (e.g. a consultant acting as scribe) may save drafts; only the
  // product owner or a global admin may submit (enforced by the API too).
  const canSubmit =
    !!product &&
    !!user &&
    (product.ownerId === user.id || user.roleId === "global_admin");

  // Shared with SDLAssessmentPanel so the two cannot disagree about who is
  // offered the control -- the previous Return to Draft button drifted out of
  // step with the API and 403'd for two of the three admin roles.
  const canReopenForEditing = canReopenSubmittedAssessment({
    status: existingAssessment?.status,
    periodIsOpen: selectedPeriodData?.isOpen,
    canManage: canManageAssessment(user, product?.ownerId),
  });

  const directoryUsers = useMemo(() => {
    const raw = ((usersData?.data as any)?.data || []) as any[];
    return raw
      .filter((u) => u?.id)
      .map((u) => ({
        id: String(u.id),
        name: String(u.name ?? ""),
        email: String(u.email ?? ""),
      }));
  }, [usersData]);

  const savedParticipants = (existingAssessment?.participants ??
    []) as AssessmentParticipant[];

  // Reset the picker whenever a different assessment or period loads. Guarded
  // on the fetch state because existingAssessment is briefly undefined while
  // refetching, which would otherwise wipe a selection the user just made.
  const savedParticipantKey = JSON.stringify(
    savedParticipants.map((p) => p.userId ?? `deleted:${p.userEmail}`),
  );
  useEffect(() => {
    if (assessmentLoading || assessmentFetching) return;
    setParticipantIds(participantIdsFrom(savedParticipants));
    // savedParticipantKey stands in for savedParticipants, which is a fresh
    // array identity on every render.
  }, [
    existingAssessment?.id,
    savedParticipantKey,
    selectedPeriod,
    assessmentLoading,
    assessmentFetching,
  ]);

  const saveMutation = useMutation(
    (payload: any) => {
      if (existingAssessmentId) {
        return apiService.updateSDLAssessment(existingAssessmentId, payload);
      }
      return apiService.createSDLAssessment(id!, payload);
    },
    {
      onSuccess: (response: any) => {
        const newId = response?.data?.data?.id;
        if (newId && !existingAssessmentId) {
          setExistingAssessmentId(newId);
          loadedAssessmentRef.current = newId;
        }
        queryClient.invalidateQueries(["sdlAssessment", id, selectedPeriod]);
        queryClient.invalidateQueries(["sdlAssessmentPeriods", id]);
        queryClient.invalidateQueries(["product", id]);
        queryClient.invalidateQueries(["assessmentStatus", id]);
        toast.success("Assessment draft saved");
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.error || "Failed to save assessment");
      },
    },
  );

  const submitMutation = useMutation(
    (payload: any) => {
      if (existingAssessmentId) {
        return apiService.updateSDLAssessment(existingAssessmentId, payload);
      }
      return apiService.createSDLAssessment(id!, payload);
    },
    {
      onSuccess: (response: any) => {
        const newId = response?.data?.data?.id;
        if (newId && !existingAssessmentId) {
          setExistingAssessmentId(newId);
          loadedAssessmentRef.current = newId;
        }
        queryClient.invalidateQueries(["sdlAssessment", id, selectedPeriod]);
        queryClient.invalidateQueries(["sdlAssessmentPeriods", id]);
        queryClient.invalidateQueries(["product", id]);
        queryClient.invalidateQueries(["assessmentStatus", id]);
        // Calculate score before switching to feedback view
        const scored = Object.values(answers).filter(
          (a) => a.score !== null && a.score !== -1,
        );
        const finalScore =
          scored.length > 0
            ? scored.reduce((acc, a) => acc + (a.score ?? 0), 0) / scored.length
            : 0;
        setSubmittedScore(finalScore);
        setShowFeedback(true);
      },
      onError: (error: any) => {
        toast.error(
          error.response?.data?.error || "Failed to submit assessment",
        );
      },
    },
  );

  // Reopening a submitted assessment so it can be corrected. The answers are
  // already loaded on screen; this only flips the status back to draft, after
  // which the normal Save/Submit controls apply.
  const reopenMutation = useMutation(
    (assessmentId: string) =>
      apiService.returnSDLAssessmentToDraft(assessmentId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["sdlAssessment", id, selectedPeriod]);
        queryClient.invalidateQueries(["sdlAssessmentPeriods", id]);
        queryClient.invalidateQueries(["product", id]);
        queryClient.invalidateQueries(["assessmentStatus", id]);
        // The assessment is now a draft, and drafts normally open on the
        // "Continue Assessment" interstitial. The user is already looking at
        // the answers, so send them straight back into the form rather than
        // bouncing them through a splash screen they did not ask for.
        setAssessmentStarted(true);
        toast.success("Assessment reopened for editing");
      },
      onError: (error: any) => {
        toast.error(
          error.response?.data?.error || "Failed to reopen assessment",
        );
      },
    },
  );

  const handleReopenForEditing = () => {
    const assessmentId = existingAssessmentId ?? existingAssessment?.id;
    if (!assessmentId) return;

    if (
      !window.confirm(
        "Reopen this assessment for editing?\n\n" +
          "It returns to draft until you submit it again, so it will show as " +
          "unsubmitted in the meantime.",
      )
    ) {
      return;
    }

    reopenMutation.mutate(assessmentId);
  };

  useEffect(() => {
    if (periodFromUrl && !selectedPeriod) {
      setSelectedPeriod(periodFromUrl);
    } else if (!selectedPeriod && openPeriods.length > 0) {
      setSelectedPeriod(openPeriods[0].period);
    }
  }, [openPeriods, selectedPeriod, periodFromUrl]);

  useEffect(() => {
    if (uiQuestions.length === 0) return;

    const initial: Record<string, AnswerState> = {};
    uiQuestions.forEach((q) => {
      initial[q.id] = { score: null, details: "" };
    });

    setAnswers((prev) => (Object.keys(prev).length ? prev : initial));
  }, [uiQuestions]);

  useEffect(() => {
    // Don't process stale/cached data while a fetch is in flight
    if (assessmentLoading || assessmentFetching) return;

    if (!existingAssessment) {
      setExistingAssessmentId(null);
      loadedAssessmentRef.current = null;
      loadedPeriodRef.current = selectedPeriod;
      // Clear answers when switching to a period with no assessment
      if (uiQuestions.length > 0) {
        const cleared: Record<string, AnswerState> = {};
        uiQuestions.forEach((q) => {
          cleared[q.id] = { score: null, details: "" };
        });
        setAnswers(cleared);
      }
      return;
    }

    // Verify the assessment belongs to the currently selected period
    const assessmentPeriod = existingAssessment.period;
    if (assessmentPeriod && assessmentPeriod !== selectedPeriod) {
      // Stale data from a different period — ignore it
      return;
    }

    const assessmentId = existingAssessment.id || null;
    setExistingAssessmentId(assessmentId);

    // For drafts, only populate answers once the user clicks "Continue"
    // For submitted (view-only), load immediately
    const isSubmitted = existingAssessment.status === "submitted";
    if (!isSubmitted && !assessmentStarted) return;

    // Skip if we already loaded this exact assessment for this exact period
    if (
      loadedAssessmentRef.current === assessmentId &&
      loadedPeriodRef.current === selectedPeriod
    )
      return;
    loadedAssessmentRef.current = assessmentId;
    loadedPeriodRef.current = selectedPeriod;

    let parsedAnswers: any[] = [];
    if (Array.isArray(existingAssessment.answers)) {
      parsedAnswers = existingAssessment.answers;
    } else if (typeof existingAssessment.answers === "string") {
      try {
        parsedAnswers = JSON.parse(existingAssessment.answers);
      } catch {
        parsedAnswers = [];
      }
    }

    const nextAnswers: Record<string, AnswerState> = {};
    uiQuestions.forEach((q) => {
      const found = parsedAnswers.find(
        (a) => Number(a.questionId) === Number(q.id),
      );
      // Treat maturityScore 0 with no details as "not answered" (legacy drafts saved 0 for unanswered)
      const hasRealAnswer =
        found &&
        typeof found.maturityScore === "number" &&
        (found.maturityScore !== 0 || (found.details && found.details.trim()));
      nextAnswers[q.id] = {
        score: hasRealAnswer ? found.maturityScore : null,
        details: found?.details || "",
      };
    });

    setAnswers(nextAnswers);
  }, [
    existingAssessment,
    uiQuestions,
    assessmentLoading,
    assessmentFetching,
    selectedPeriod,
    assessmentStarted,
  ]);

  // Parse previous assessment answers
  useEffect(() => {
    const prevAssessment = (previousAssessmentData?.data as any)?.data;
    if (!prevAssessment || uiQuestions.length === 0) {
      setPreviousAnswers({});
      setPreviousPeriod(null);
      return;
    }

    setPreviousPeriod(prevAssessment.period || null);

    let parsedPrev: any[] = [];
    if (Array.isArray(prevAssessment.answers)) {
      parsedPrev = prevAssessment.answers;
    } else if (typeof prevAssessment.answers === "string") {
      try {
        parsedPrev = JSON.parse(prevAssessment.answers);
      } catch {
        parsedPrev = [];
      }
    }

    const prev: Record<string, AnswerState> = {};
    uiQuestions.forEach((q) => {
      const found = parsedPrev.find(
        (a: any) => Number(a.questionId) === Number(q.id),
      );
      const hasRealAnswer =
        found &&
        typeof found.maturityScore === "number" &&
        (found.maturityScore !== 0 || (found.details && found.details.trim()));
      prev[q.id] = {
        score: hasRealAnswer ? found.maturityScore : null,
        details: found?.details || "",
      };
    });
    setPreviousAnswers(prev);
  }, [previousAssessmentData, uiQuestions]);

  const handleChange = (
    questionId: string,
    field: "score" | "details",
    value: any,
  ) => {
    setAnswers((prev) => {
      const current = prev[questionId] ?? { score: null, details: "" };
      return {
        ...prev,
        [questionId]: { ...current, [field]: value } as AnswerState,
      };
    });
  };

  const calculateScore = () => {
    const scored = Object.values(answers).filter(
      (a) => a.score !== null && a.score !== -1,
    );
    if (scored.length === 0) return null;
    const sum = scored.reduce((acc, a) => acc + (a.score ?? 0), 0);
    return sum / scored.length;
  };

  const buildPayload = (status: "draft" | "submitted") => {
    const payloadAnswers = uiQuestions.map((q) => {
      const score = answers[q.id]?.score;
      return {
        questionId: Number(q.id),
        maturityScore: score !== null && score !== undefined ? score : null, // -1 = N/A, 0-4 = maturity
        details: answers[q.id]?.details || "",
        evidence: "",
      };
    });

    return {
      period: selectedPeriod,
      version: "1.0",
      status,
      answers: payloadAnswers,
      participantIds,
    };
  };

  const handleSave = () => {
    if (!selectedPeriod) {
      toast.error("No open assessment period is available");
      return;
    }
    if (isLockedDraft) {
      toast.error("This assessment period is closed. Changes cannot be saved.");
      return;
    }

    saveMutation.mutate(buildPayload("draft"));
  };

  const handleSubmit = () => {
    if (!selectedPeriod) {
      toast.error("No open assessment period is available");
      return;
    }
    if (isLockedDraft) {
      toast.error(
        "This assessment period is closed. Assessment cannot be submitted.",
      );
      return;
    }

    const unanswered = uiQuestions.filter(
      (q) =>
        answers[q.id]?.score === null || answers[q.id]?.score === undefined,
    );

    const missingDetails = uiQuestions.filter(
      (q) => !(answers[q.id]?.details || "").trim(),
    );

    if (unanswered.length > 0) {
      toast.error(
        `Please answer all questions. ${unanswered.length} remaining.`,
      );
      return;
    }

    if (missingDetails.length > 0) {
      toast.error("Please add implementation details for all questions.");
      return;
    }

    submitMutation.mutate(buildPayload("submitted"));
  };

  if (productLoading || periodsLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Product not found</p>
      </div>
    );
  }

  // Feedback screen after submission
  if (showFeedback && product) {
    const feedbackAnswers = uiQuestions
      .filter((q) => answers[q.id]?.score !== -1) // Exclude N/A questions
      .map((q) => ({
        questionId: q.id,
        name: q.name,
        practice: q.practice,
        score: answers[q.id]?.score ?? 0,
        details: answers[q.id]?.details ?? "",
      }));

    return (
      <div className="max-w-full -mx-4 sm:-mx-6 lg:-mx-8 -my-6 px-4 sm:px-6 lg:px-8 py-3 flex flex-col h-[calc(100vh-7rem)] lg:h-[calc(100vh-3rem)] overflow-hidden">
        {/* Header */}
        <div className="flex items-center space-x-3 flex-shrink-0">
          <button
            onClick={() => navigate(`/products/${id}`)}
            className="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-lg font-bold text-gray-900 dark:text-gray-100 leading-tight">
              SDL Assessment Results
            </h1>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {product.name} &mdash; {selectedPeriod}
            </p>
          </div>
        </div>

        <SDLAssessmentFeedback
          productId={id!}
          productName={product.name}
          period={selectedPeriod || ""}
          overallScore={submittedScore}
          answers={feedbackAnswers}
          previousAnswers={previousAnswers}
          previousPeriod={previousPeriod}
        />
      </div>
    );
  }

  const overallScore = calculateScore();
  const answeredCount = Object.values(answers).filter(
    (a) => a.score !== null,
  ).length;
  const naCount = Object.values(answers).filter((a) => a.score === -1).length;
  const activeQuestion = uiQuestions[activeTab] || uiQuestions[0];

  if (!activeQuestion) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">No SDL questions available</p>
      </div>
    );
  }

  // Participants are gathered on the start screen, where there is room for the
  // picker, rather than alongside the questions where it would permanently
  // shrink the answering area. The question view keeps a collapsed summary so
  // the list stays correctable without being in the way.
  const participantsEditor = (
    <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/30 p-5">
      <div className="flex items-center gap-2.5 mb-3">
        <div className="w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900/40 flex items-center justify-center shadow-sm flex-shrink-0">
          <Users className="h-5 w-5 text-blue-600 dark:text-blue-400" />
        </div>
        <div>
          <span className="font-semibold text-gray-700 dark:text-gray-300 text-base">
            Participants
          </span>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Who took part in this assessment. You can change this later.
          </p>
        </div>
      </div>
      <ParticipantMultiSelect
        selectedIds={participantIds}
        onChange={setParticipantIds}
        users={directoryUsers}
        loading={usersLoading}
      />
    </div>
  );

  const participantSummary =
    participantIds.length === 0
      ? "None recorded"
      : participantIds
          .map((pid) => directoryUsers.find((u) => u.id === pid)?.name ?? pid)
          .join(", ");

  return (
    <div className="max-w-full -mx-4 sm:-mx-6 lg:-mx-8 -my-6 px-4 sm:px-6 lg:px-8 py-3 flex flex-col h-[calc(100vh-7rem)] lg:h-[calc(100vh-3rem)] overflow-hidden">
      {/* Header */}
      <div className="flex items-center space-x-3 flex-shrink-0">
        <button
          onClick={() => navigate(`/products/${id}`)}
          className="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div className="flex-1 min-w-0">
          <h1 className="text-lg font-bold text-gray-900 dark:text-gray-100 leading-tight">
            SDL Assessment
          </h1>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {product.name} &mdash; Security Development Lifecycle
          </p>
        </div>
      </div>

      {/* Progress */}
      <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 px-4 py-2 flex-shrink-0 mt-2">
        {isViewOnly && (
          <div className="flex items-center gap-1.5 text-xs text-amber-600 dark:text-amber-400 mb-1">
            <Lock className="h-3 w-3" />
            <span>
              {canReopenForEditing
                ? "This assessment has been submitted. Choose Edit Assessment to correct it."
                : "This assessment has been submitted and is read-only"}
            </span>
          </div>
        )}
        {isLockedDraft && (
          <div className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400 mb-1">
            <Lock className="h-3 w-3" />
            <span>This draft is locked — the assessment period has closed</span>
          </div>
        )}
        <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
          <span>
            Period:{" "}
            <span className="font-medium text-gray-700 dark:text-gray-200">
              {selectedPeriod}
            </span>
          </span>
          <span>
            {answeredCount}/{uiQuestions.length} answered
            {naCount > 0 && ` · ${naCount} N/A`}
            {overallScore !== null && ` · Score: ${overallScore.toFixed(2)}/4`}
          </span>
        </div>
        <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1.5 mt-1.5">
          <div
            className="bg-green-600 h-1.5 rounded-full transition-all"
            style={{
              width: `${uiQuestions.length ? (answeredCount / uiQuestions.length) * 100 : 0}%`,
            }}
          />
        </div>
      </div>

      {/* Main Content */}
      {assessmentLoading ? (
        <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 mt-2 flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600 mx-auto"></div>
            <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
              Loading assessment...
            </p>
          </div>
        </div>
      ) : !hasAssessmentForPeriod && !selectedPeriodData?.isOpen ? (
        /* No assessment and period is closed */
        <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 mt-2 flex-1 flex items-center justify-center">
          <div className="text-center max-w-md px-6">
            <div className="w-16 h-16 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center mx-auto mb-4">
              <Lock className="h-8 w-8 text-gray-400 dark:text-gray-500" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
              No Assessment Submitted
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
              No SDL assessment was submitted for{" "}
              <span className="font-medium">{selectedPeriod}</span>. This period
              is now closed.
            </p>
            <button
              onClick={() => navigate(`/products/${id}`)}
              className="btn btn-outline text-sm"
            >
              <ArrowLeft className="h-4 w-4 mr-1" />
              Back to Product
            </button>
          </div>
        </div>
      ) : isLockedDraft && !assessmentStarted ? (
        /* Locked draft – period closed, show view-only landing */
        <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 mt-2 flex-1 flex items-center justify-center">
          <div className="text-center max-w-md px-6">
            <div className="w-16 h-16 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center mx-auto mb-4">
              <Lock className="h-8 w-8 text-gray-400 dark:text-gray-500" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
              Draft Locked
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
              This draft assessment for{" "}
              <span className="font-medium">{selectedPeriod}</span> is locked
              because the assessment period has closed. You can view the saved
              answers but cannot make changes.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <button
                onClick={() => navigate(`/products/${id}`)}
                className="btn btn-outline text-sm w-full sm:w-auto"
              >
                <ArrowLeft className="h-4 w-4 mr-1" />
                Back to Product
              </button>
              <button
                onClick={() => setAssessmentStarted(true)}
                className="btn btn-outline text-sm w-full sm:w-auto"
              >
                <Eye className="h-4 w-4 mr-1" />
                View Draft
              </button>
            </div>
          </div>
        </div>
      ) : hasAssessmentForPeriod &&
        existingAssessment?.status === "draft" &&
        !assessmentStarted ? (
        /* Draft exists but user hasn't clicked Continue yet */
        <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 mt-2 flex-1 flex items-center justify-center">
          <div className="text-center max-w-lg px-6">
            <div className="w-20 h-20 rounded-full bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center mx-auto mb-5">
              <ClipboardList className="h-10 w-10 text-blue-600 dark:text-blue-400" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
              Continue SDL Assessment
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">
              Period:{" "}
              <span className="font-semibold text-gray-700 dark:text-gray-200">
                {selectedPeriod}
              </span>
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-6 leading-relaxed">
              You have a draft assessment in progress for this period. Your
              previously saved answers will be loaded when you continue.
            </p>
            <div className="text-left mb-6">{participantsEditor}</div>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <button
                onClick={() => navigate(`/products/${id}`)}
                className="btn btn-outline text-sm w-full sm:w-auto"
              >
                <ArrowLeft className="h-4 w-4 mr-1" />
                Back to Product
              </button>
              <button
                onClick={() => setAssessmentStarted(true)}
                className="btn btn-primary text-sm w-full sm:w-auto"
              >
                <PlayCircle className="h-4 w-4 mr-1" />
                Continue Assessment
              </button>
            </div>
          </div>
        </div>
      ) : !hasAssessmentForPeriod &&
        selectedPeriodData?.isOpen &&
        !assessmentStarted ? (
        /* Open period, no assessment yet — show confirmation screen */
        <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 mt-2 flex-1 overflow-y-auto">
          <div className="px-10 py-10">
            {/* Hero header with gradient accent */}
            {(() => {
              const pillBase =
                "inline-flex items-center gap-1.5 px-3 py-1 rounded-full font-medium text-sm";
              const pillLight = `${pillBase} bg-white/70 dark:bg-gray-800/70 border border-green-200/60 dark:border-green-700/40 text-gray-700 dark:text-gray-300`;
              return (
                <div className="relative rounded-xl bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50 dark:from-green-900/20 dark:via-emerald-900/15 dark:to-teal-900/10 border border-green-100 dark:border-green-800/30 p-7 mb-8 overflow-hidden">
                  <div className="absolute top-0 right-0 w-48 h-48 bg-green-200/20 dark:bg-green-500/5 rounded-full -translate-y-1/2 translate-x-1/3" />
                  <div className="absolute bottom-0 left-0 w-32 h-32 bg-teal-200/20 dark:bg-teal-500/5 rounded-full translate-y-1/2 -translate-x-1/3" />
                  <div className="relative flex items-center gap-5">
                    <div className="w-16 h-16 rounded-xl bg-white dark:bg-gray-800 shadow-sm border border-green-200 dark:border-green-700/50 flex items-center justify-center flex-shrink-0">
                      <ClipboardList className="h-8 w-8 text-green-600 dark:text-green-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-1.5">
                        Start SDL Assessment
                      </h3>
                      <div className="flex items-center gap-3 flex-wrap">
                        <span className={pillLight}>📅 {selectedPeriod}</span>
                        <span className={pillLight}>
                          📝 {uiQuestions.length} questions
                        </span>
                        <span
                          className={`${pillBase} bg-green-100/80 dark:bg-green-800/40 border border-green-300/60 dark:border-green-600/40 text-green-700 dark:text-green-300`}
                        >
                          ✅ Draft & submit supported
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* Main content — 3-column grid */}
            {(() => {
              const iconBox =
                "w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 shadow-sm";
              const cards: {
                emoji: string;
                title: string;
                text: React.ReactNode;
                border: string;
                bg: string;
                iconBg: string;
              }[] = [
                {
                  emoji: "📋",
                  title: "About",
                  text: (
                    <>
                      You are about to begin the Security Development Lifecycle
                      assessment for this period. The assessment has{" "}
                      <span className="font-semibold text-gray-700 dark:text-gray-200">
                        {uiQuestions.length} questions
                      </span>{" "}
                      across multiple security practices. You can save your
                      progress as a draft and submit when ready.
                    </>
                  ),
                  border: "border-blue-100 dark:border-blue-800/40",
                  bg: "from-blue-50/80 to-white dark:from-blue-900/15 dark:to-gray-900",
                  iconBg: "bg-blue-100 dark:bg-blue-900/40",
                },
                {
                  emoji: "🎯",
                  title: "Purpose",
                  text: "The goal is not to audit or penalize, but to give you and the organization a clear picture of where your SDL is strong and where investment is needed most. Honest responses are more valuable than optimistic ones.",
                  border: "border-green-100 dark:border-green-800/40",
                  bg: "from-green-50/80 to-white dark:from-green-900/15 dark:to-gray-900",
                  iconBg: "bg-green-100 dark:bg-green-900/40",
                },
                {
                  emoji: "⚠️",
                  title: "Scoping out controls",
                  text: "You may answer N/A if a control genuinely does not apply. However, N/A is not a free pass — for every control you scope out, provide a written justification based on your threat model or product security context.",
                  border: "border-amber-200 dark:border-amber-700/40",
                  bg: "from-amber-50/80 to-white dark:from-amber-900/15 dark:to-gray-900",
                  iconBg: "bg-amber-100 dark:bg-amber-900/40",
                },
              ];
              return (
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mb-8">
                  {cards.map((c) => (
                    <div
                      key={c.title}
                      className={`rounded-xl border ${c.border} bg-gradient-to-b ${c.bg} p-6 flex flex-col`}
                    >
                      <div className="flex items-center gap-3 mb-4">
                        <div className={`${iconBox} ${c.iconBg}`}>
                          <span className="text-lg">{c.emoji}</span>
                        </div>
                        <h4 className="text-base font-bold text-gray-900 dark:text-gray-100">
                          {c.title}
                        </h4>
                      </div>
                      <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed flex-1">
                        {c.text}
                      </p>
                    </div>
                  ))}
                </div>
              );
            })()}

            {/* Resources — compact inline */}
            {(() => {
              const linkStyle =
                "inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm text-blue-700 dark:text-blue-300 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800/40 hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors shadow-sm";
              const resources = [
                {
                  label: "SDL framework",
                  href: "https://dev.sandvik/docs/default/Component/sdl-policy",
                },
                {
                  label: "Secure Dev Procedure",
                  href: "https://sandvik.sharepoint.com/sites/sandvik/Intranet%20document/Group%20Functions/Group%20-%20Policies%20and%20Procedures/Informations%20security%20policy/3.4.8.1.8%20Secure%20Development%20Procedure.pdf",
                },
              ];
              return (
                <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/30 p-5 flex flex-col sm:flex-row sm:items-center gap-4 mb-10">
                  <div className="flex items-center gap-2.5 flex-shrink-0">
                    <div className="w-10 h-10 rounded-lg bg-purple-100 dark:bg-purple-900/40 flex items-center justify-center shadow-sm">
                      <span className="text-lg">📚</span>
                    </div>
                    <span className="font-semibold text-gray-700 dark:text-gray-300 text-base">
                      SDL Resources:
                    </span>
                  </div>
                  <div className="flex flex-wrap items-center gap-3">
                    {resources.map((r) => (
                      <a
                        key={r.label}
                        href={r.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={linkStyle}
                      >
                        ↗ {r.label}
                      </a>
                    ))}
                  </div>
                </div>
              );
            })()}

            <div className="mb-10">{participantsEditor}</div>

            {/* Actions — centered */}
            <div className="flex items-center justify-center gap-4">
              <button
                onClick={() => navigate(`/products/${id}`)}
                className="btn btn-outline text-sm px-5 py-2"
              >
                <ArrowLeft className="h-4 w-4 mr-1.5" />
                Back to Product
              </button>
              <button
                onClick={() => setAssessmentStarted(true)}
                className="btn btn-primary text-sm px-5 py-2 shadow-md shadow-green-200 dark:shadow-green-900/30"
              >
                <PlayCircle className="h-4 w-4 mr-1.5" />
                Begin Assessment
              </button>
            </div>
          </div>
        </div>
      ) : (
        /* Assessment exists OR user confirmed start */
        <>
          {/* Question Navigation Dots */}
          <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 mt-2 flex-1 flex flex-col min-h-0">
            <div className="border-b border-gray-200 dark:border-gray-700 px-3 py-2 flex-shrink-0">
              <div className="flex items-center justify-center flex-wrap gap-1">
                {uiQuestions.map((q, index) => {
                  const isNA = answers[q.id]?.score === -1;
                  const isAnswered =
                    !isNA &&
                    answers[q.id]?.score !== null &&
                    answers[q.id]?.score !== undefined;
                  const isActive = index === activeTab;
                  return (
                    <button
                      key={q.id}
                      onClick={() => setActiveTab(index)}
                      title={`${index + 1}. ${q.name}${isNA ? " (N/A)" : ""}`}
                      className={`inline-flex items-center justify-center w-7 h-7 rounded-full text-[11px] font-bold transition-all ${
                        isNA
                          ? "bg-gray-200 dark:bg-gray-600 text-gray-500 dark:text-gray-400 ring-1 ring-gray-300 dark:ring-gray-500"
                          : isAnswered
                            ? "bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300 ring-1 ring-green-300 dark:ring-green-700"
                            : isActive
                              ? "bg-green-600 text-white ring-1 ring-green-400"
                              : "bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600"
                      }`}
                    >
                      {isNA ? "—" : isAnswered ? "\u2713" : index + 1}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Active question */}
            <div className="px-4 py-3 flex-1 overflow-y-auto min-h-0">
              <SDLAssessmentQuestion
                key={activeQuestion.id}
                index={activeTab}
                question={activeQuestion}
                value={answers[activeQuestion.id]?.score ?? null}
                details={answers[activeQuestion.id]?.details ?? ""}
                onChange={handleChange}
                disabled={!canEdit}
                previousAnswer={previousAnswers[activeQuestion.id] || null}
                previousPeriod={previousPeriod}
              />
            </div>

            {/* Previous / Next — pinned at bottom, always visible */}
            <div className="flex items-center justify-between px-4 py-2.5 border-t border-gray-200 dark:border-gray-700 flex-shrink-0">
              <button
                onClick={() => setActiveTab((t) => Math.max(0, t - 1))}
                disabled={activeTab === 0}
                className="btn btn-outline text-sm disabled:opacity-40"
              >
                <ChevronLeft className="h-4 w-4 mr-1" /> Previous
              </button>
              <span className="text-sm text-gray-500 dark:text-gray-400">
                {activeTab + 1} / {uiQuestions.length}
              </span>
              <button
                onClick={() =>
                  setActiveTab((t) => Math.min(uiQuestions.length - 1, t + 1))
                }
                disabled={activeTab === uiQuestions.length - 1}
                className="btn btn-outline text-sm disabled:opacity-40"
              >
                Next <ChevronRight className="h-4 w-4 ml-1" />
              </button>
            </div>
          </div>

          {/* Participants — collapsed to a single line here so the question
              area keeps its height. Captured on the start screen; this is the
              correction path. */}
          <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 px-4 py-2 flex-shrink-0 mt-2">
            <button
              type="button"
              onClick={() => setParticipantsExpanded((v) => !v)}
              aria-expanded={participantsExpanded}
              className="w-full flex items-center gap-2 text-left"
            >
              <Users className="h-4 w-4 text-gray-500 dark:text-gray-400 flex-shrink-0" />
              <span className="text-xs font-semibold text-gray-700 dark:text-gray-200 flex-shrink-0">
                Participants
              </span>
              <span className="text-xs text-gray-500 dark:text-gray-400 truncate flex-1 min-w-0">
                {canEdit
                  ? participantSummary
                  : savedParticipants.length > 0
                    ? savedParticipants.map(participantLabel).join(", ")
                    : "None recorded"}
              </span>
              <ChevronRight
                className={`h-4 w-4 text-gray-400 flex-shrink-0 transition-transform ${
                  participantsExpanded ? "rotate-90" : ""
                }`}
              />
            </button>

            {participantsExpanded && (
              <div className="mt-2 pt-2 border-t border-gray-200 dark:border-gray-700">
                {canEdit ? (
                  <ParticipantMultiSelect
                    selectedIds={participantIds}
                    onChange={setParticipantIds}
                    users={directoryUsers}
                    loading={usersLoading}
                    dropUp
                  />
                ) : savedParticipants.length > 0 ? (
                  <ul className="flex flex-wrap gap-2">
                    {savedParticipants.map((p) => (
                      <li
                        key={p.userId ?? p.userEmail}
                        className="inline-flex items-center px-2 py-1 text-sm rounded-full bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200"
                      >
                        {participantLabel(p)}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    No participants were recorded for this assessment.
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 px-4 py-2.5 flex items-center justify-between flex-shrink-0 mt-2">
            {isViewOnly || isLockedDraft ? (
              <>
                <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                  {isLockedDraft ? (
                    <>
                      <Lock className="h-4 w-4 text-gray-400" />
                      <span>
                        Locked Draft
                        {overallScore !== null
                          ? ` · Score: ${overallScore.toFixed(2)}/4`
                          : ""}
                      </span>
                    </>
                  ) : (
                    <>
                      <CheckCircle className="h-4 w-4 text-green-500" />
                      <span>
                        Score:{" "}
                        {overallScore !== null
                          ? `${overallScore.toFixed(2)}/4`
                          : "N/A"}
                      </span>
                    </>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {canReopenForEditing && (
                    <button
                      onClick={handleReopenForEditing}
                      disabled={reopenMutation.isLoading}
                      className="btn btn-outline"
                    >
                      <Edit className="h-4 w-4 mr-2" />
                      {reopenMutation.isLoading
                        ? "Reopening..."
                        : "Edit Assessment"}
                    </button>
                  )}
                  <button
                    onClick={() => navigate(`/products/${id}`)}
                    className="btn btn-outline"
                  >
                    <ArrowLeft className="h-4 w-4 mr-2" />
                    Back to Product
                  </button>
                </div>
              </>
            ) : (
              <>
                <button
                  onClick={handleSave}
                  disabled={
                    !selectedPeriod ||
                    saveMutation.isLoading ||
                    submitMutation.isLoading
                  }
                  className="btn btn-outline"
                >
                  <Save className="h-4 w-4 mr-2" />
                  {saveMutation.isLoading ? "Saving..." : "Save Draft"}
                </button>
                {canSubmit ? (
                  <button
                    onClick={handleSubmit}
                    disabled={
                      !selectedPeriod ||
                      saveMutation.isLoading ||
                      submitMutation.isLoading
                    }
                    className="btn btn-primary"
                  >
                    <CheckCircle className="h-4 w-4 mr-2" />
                    {submitMutation.isLoading
                      ? "Submitting..."
                      : "Submit Assessment"}
                  </button>
                ) : (
                  <span
                    className="self-center text-xs text-gray-500 dark:text-gray-400"
                    title="Only the product owner can submit"
                  >
                    Save a draft — the product owner will review and submit.
                  </span>
                )}
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
}
